/**
 * DailyBriefingCard — FutureBot's morning briefing, first thing on the CRM
 * Dashboard.
 *
 * Renders today's briefing from futurebot_briefings (generated 7am ET
 * weekdays; on-demand for new users / regenerate), with the AI narrative,
 * the ranked top-5 call list (deep links), and section count chips. Collapse
 * state persists to users/{uid}.crmPreferences.dailyBriefingCollapsed and the
 * email opt-in writes crmNotificationSettings.dailyBriefing.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Skeleton,
  Switch,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiDollarSign,
  FiPhoneCall,
  FiRefreshCw,
  FiSunrise,
  FiTrendingUp,
  FiUserPlus,
  FiZap,
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, updateDoc } from 'firebase/firestore';
import { app, db } from '../../../utils/init-firebase';

const functions = getFunctions(app, 'us-east1');

const CALL_TYPE_META = {
  new_lead: { icon: FiUserPlus, color: 'blue.400', label: 'New lead' },
  overdue_action: { icon: FiClock, color: 'orange.400', label: 'Overdue' },
  refi_opportunity: { icon: FiDollarSign, color: 'green.400', label: 'Refi' },
  stagnant: { icon: FiTrendingUp, color: 'purple.400', label: 'Stagnant' },
};

function greetingFor(now = new Date()) {
  const hour = Number(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour: 'numeric', hourCycle: 'h23',
  }).format(now));
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function DailyBriefingCard({ currentUser }) {
  const toast = useToast();
  const history = useHistory();

  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [collapsed, setCollapsed] = useState(
    currentUser?.crmPreferences?.dailyBriefingCollapsed === true
  );
  const [emailOptIn, setEmailOptIn] = useState(
    currentUser?.crmNotificationSettings?.dailyBriefing === true
  );
  const fetchedRef = useRef(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('blue.200', 'blue.700');
  const accentBg = useColorModeValue('blue.50', 'rgba(66, 153, 225, 0.12)');
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const itemHover = useColorModeValue('gray.50', 'gray.700');
  const chipBg = useColorModeValue('gray.100', 'gray.700');

  const uid = currentUser?.uid;
  const firstName = (currentUser?.Name || currentUser?.name || '').split(' ')[0] || null;

  const fetchBriefing = useCallback(async ({ generate = false } = {}) => {
    if (generate) setGenerating(true);
    else setLoading(true);
    try {
      const call = httpsCallable(functions, 'getDailyBriefing');
      const res = await call({ generate, markViewed: true });
      setBriefing(res.data?.briefing || null);
      return res.data?.briefing || null;
    } catch (err) {
      console.error('getDailyBriefing failed:', err);
      if (generate) {
        toast({ title: 'Could not build your briefing', description: err.message, status: 'error', duration: 6000, isClosable: true });
      }
      return null;
    } finally {
      setLoading(false);
      setGenerating(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!uid || fetchedRef.current) return;
    fetchedRef.current = true;
    fetchBriefing();
  }, [uid, fetchBriefing]);

  const toggleCollapsed = useCallback(async () => {
    const next = !collapsed;
    setCollapsed(next);
    if (!uid) return;
    try {
      await updateDoc(doc(db, 'users', uid), { 'crmPreferences.dailyBriefingCollapsed': next });
    } catch (err) {
      console.warn('Failed to persist briefing collapse state:', err.message);
    }
  }, [collapsed, uid]);

  const toggleEmail = useCallback(async (checked) => {
    setEmailOptIn(checked);
    if (!uid) return;
    try {
      await updateDoc(doc(db, 'users', uid), { 'crmNotificationSettings.dailyBriefing': checked });
      toast({
        title: checked ? 'Morning email on' : 'Morning email off',
        description: checked ? 'You\u2019ll get your briefing by email at 7am ET on weekdays.' : undefined,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      setEmailOptIn(!checked);
      toast({ title: 'Could not update the setting', description: err.message, status: 'error', duration: 5000, isClosable: true });
    }
  }, [uid, toast]);

  const sections = briefing?.sections || {};
  const counts = sections.counts || {};
  const callList = sections.callList || [];

  const briefingDateLabel = briefing?.briefing_date
    ? new Date(`${briefing.briefing_date}T12:00:00Z`).toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric',
      })
    : new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const chips = [
    { key: 'newLeads', label: 'new leads', count: counts.newLeads, route: '/crm/my-deals' },
    { key: 'tasks', label: 'tasks due', count: (counts.tasksOverdue || 0) + (counts.tasksDueToday || 0), route: '/tasks' },
    { key: 'nextActionsDue', label: 'actions due', count: counts.nextActionsDue, route: '/crm/my-deals' },
    { key: 'refiOpportunities', label: 'refi opportunities', count: counts.refiOpportunities, route: '/crm/mortgage-monitor' },
    { key: 'stagnant', label: 'going cold', count: counts.stagnant, route: '/crm/my-deals' },
  ].filter((c) => (c.count || 0) > 0);

  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      overflow="hidden"
      shadow="sm"
    >
      {/* Header */}
      <Flex px={5} py={3} bg={accentBg} justify="space-between" align="center">
        <HStack spacing={3}>
          <Icon as={FiSunrise} boxSize={5} color="orange.400" />
          <Box>
            <Heading size="sm">
              {greetingFor()}{firstName ? `, ${firstName}` : ''} — here&apos;s your briefing
            </Heading>
            <Text fontSize="xs" color={subtleText}>{briefingDateLabel} · from FutureBot</Text>
          </Box>
        </HStack>
        <HStack spacing={1}>
          <Tooltip label="Regenerate with fresh data">
            <IconButton
              aria-label="Regenerate briefing"
              icon={<FiRefreshCw />}
              size="sm"
              variant="ghost"
              isLoading={generating}
              onClick={() => fetchBriefing({ generate: true })}
            />
          </Tooltip>
          <IconButton
            aria-label={collapsed ? 'Expand briefing' : 'Collapse briefing'}
            icon={collapsed ? <FiChevronDown /> : <FiChevronUp />}
            size="sm"
            variant="ghost"
            onClick={toggleCollapsed}
          />
        </HStack>
      </Flex>

      <Collapse in={!collapsed} animateOpacity>
        <Box px={5} py={4}>
          {loading ? (
            <VStack align="stretch" spacing={3}>
              <Skeleton h="16px" borderRadius="md" />
              <Skeleton h="16px" w="80%" borderRadius="md" />
              <Skeleton h="60px" borderRadius="md" />
            </VStack>
          ) : !briefing ? (
            <VStack align="stretch" spacing={3} py={2}>
              <Text fontSize="sm" color={subtleText}>
                No briefing yet for today — it generates automatically at 7am ET on weekdays, or build one now.
              </Text>
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<FiZap />}
                alignSelf="flex-start"
                isLoading={generating}
                onClick={() => fetchBriefing({ generate: true })}
              >
                Build my briefing
              </Button>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              {/* Narrative */}
              {briefing.narrative && (
                <Text fontSize="sm" lineHeight="1.7">{briefing.narrative}</Text>
              )}

              {/* Section chips */}
              {chips.length > 0 && (
                <Flex gap={2} wrap="wrap">
                  {chips.map((chip) => (
                    <Badge
                      key={chip.key}
                      bg={chipBg}
                      px={2.5}
                      py={1}
                      borderRadius="full"
                      cursor="pointer"
                      textTransform="none"
                      fontWeight="500"
                      onClick={() => history.push(chip.route)}
                      _hover={{ opacity: 0.75 }}
                    >
                      {chip.count} {chip.label}
                    </Badge>
                  ))}
                </Flex>
              )}

              {/* Call list */}
              {callList.length > 0 && (
                <Box>
                  <HStack mb={2} spacing={2}>
                    <Icon as={FiPhoneCall} boxSize={4} color="blue.400" />
                    <Text fontSize="sm" fontWeight="700">Call these first</Text>
                  </HStack>
                  <VStack align="stretch" spacing={1}>
                    {callList.map((item) => {
                      const meta = CALL_TYPE_META[item.type] || CALL_TYPE_META.new_lead;
                      return (
                        <Flex
                          key={`${item.rank}-${item.label}`}
                          align="center"
                          gap={3}
                          px={3}
                          py={2}
                          borderRadius="md"
                          cursor="pointer"
                          _hover={{ bg: itemHover }}
                          onClick={() => item.route && history.push(item.route)}
                        >
                          <Text fontWeight="800" color={subtleText} w="18px">{item.rank}</Text>
                          <Icon as={meta.icon} color={meta.color} boxSize={4} flexShrink={0} />
                          <Box flex={1} minW={0}>
                            <Text fontSize="sm" fontWeight="600" noOfLines={1}>{item.label}</Text>
                            <Text fontSize="xs" color={subtleText} noOfLines={1}>{item.detail}</Text>
                          </Box>
                          <Badge variant="subtle" fontSize="0.6rem" flexShrink={0}>{meta.label}</Badge>
                        </Flex>
                      );
                    })}
                  </VStack>
                </Box>
              )}

              {/* Wins */}
              {(sections.wins || []).length > 0 && (
                <HStack spacing={2} fontSize="sm">
                  <Icon as={FiCheckCircle} color="green.400" />
                  <Text color={subtleText}>
                    Recent wins: {(sections.wins || []).map((w) => w.name).join(', ')}
                  </Text>
                </HStack>
              )}

              {/* Footer */}
              <Flex justify="space-between" align="center" pt={1} wrap="wrap" gap={2}>
                <FormControl display="flex" alignItems="center" w="auto">
                  <Switch
                    id="briefing-email-optin"
                    size="sm"
                    colorScheme="teal"
                    isChecked={emailOptIn}
                    onChange={(e) => toggleEmail(e.target.checked)}
                  />
                  <FormLabel htmlFor="briefing-email-optin" mb={0} ml={2} fontSize="xs" color={subtleText}>
                    Email me this every morning
                  </FormLabel>
                </FormControl>
                <Text fontSize="xs" color={subtleText}>
                  {briefing.generated_at
                    ? `Generated ${new Date(briefing.generated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
                    : ''}
                </Text>
              </Flex>
            </VStack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
